import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
    MappingRefreshWidgetProps,
    MappingSyncStatus,
    TClientTimezone,
} from "@ts/common-types";
import { mapStatusToSyncStatus, formatTimestampForDisplay } from "@/ts/utils";
import { STATUS_COLORS, STATUS_LABELS } from "@/ts/constants";
import { showToast } from "@/ts/modals";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
    getMappingRefreshStatus,
    triggerMappingRefresh,
} from "@/ts/retrieve-data";

dayjs.extend(utc);
dayjs.extend(timezone);

const MappingRefreshWidget: React.FC<MappingRefreshWidgetProps> = ({
    status = MappingSyncStatus.Success,
    statusText,
    lastSyncText = "...",
    buttonText = "Sync tagging",
    className = "",
    style,
}) => {
    const [mappingRefreshStatus, setMappingRefreshStatus] =
        useState<MappingSyncStatus | null>(null);
    const [syncTimeConfig, setSyncTimeConfig] = useState<{
        startTime: number | null;
        endTime: number | null;
    }>({
        startTime: null,
        endTime: null,
    });
    const [isRefreshing, setIsRefreshing] = useState(false);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isPollingRef = useRef(false);
    const isMountedRef = useRef(true);

    const clientTimezone = useSelector(
        (state: TClientTimezone) => state.clientTimezone.value
    );

    const currentStatus = mappingRefreshStatus || status;
    const color = STATUS_COLORS[currentStatus];
    const label = statusText || STATUS_LABELS[currentStatus];

    const formattedEndTime = syncTimeConfig.endTime
        ? formatTimestampForDisplay(
            syncTimeConfig.endTime,
            clientTimezone.value
        )
        : lastSyncText;

    const tooltipContent =
        syncTimeConfig.startTime && syncTimeConfig.endTime ? (
            <div className="d-flex flex-column gap-2 p-2">
                <div className="d-flex align-items-center">
                    <span
                        className="fw-semibold me-2"
                        style={{ minWidth: "60px" }}
                    >
                        Started:
                    </span>
                    <span>
                        {formatTimestampForDisplay(
                            syncTimeConfig.startTime,
                            clientTimezone.value
                        )}
                    </span>
                </div>
                <div className="d-flex align-items-center">
                    <span
                        className="fw-semibold me-2"
                        style={{ minWidth: "60px" }}
                    >
                        Ended:
                    </span>
                    <span>
                        {formatTimestampForDisplay(
                            syncTimeConfig.endTime,
                            clientTimezone.value
                        )}
                    </span>
                </div>
            </div>
        ) : null;

    const pollStatus = useCallback(async () => {
        if (isPollingRef.current) return;

        isPollingRef.current = true;

        try {
            const response = await getMappingRefreshStatus();
            if (!isMountedRef.current) return;

            if (response?.length) {
                const {
                    startTime: apiStartTime,
                    endTime: apiEndTime,
                    status: apiStatus,
                } = response[0];
                const mappedStatus = mapStatusToSyncStatus(apiStatus);
                setMappingRefreshStatus(mappedStatus);
                setSyncTimeConfig({
                    startTime: apiStartTime,
                    endTime: apiEndTime,
                });

                if (mappedStatus !== MappingSyncStatus.Syncing) {
                    intervalRef.current && clearInterval(intervalRef.current);
                }
            } else {
                // No data available - hide the status and stop polling
                setMappingRefreshStatus(null);
                intervalRef.current && clearInterval(intervalRef.current);
            }
        } catch (err) {
            if (isMountedRef.current) {
                console.error("Polling error:", err);
                setMappingRefreshStatus(MappingSyncStatus.Error);
            }
        } finally {
            isPollingRef.current = false;
        }
    }, []);

    const handleRefreshClick = async () => {
        setIsRefreshing(true);
        try {
            const response = await triggerMappingRefresh();

            if (response && response.success) {
                showToast(
                    "success",
                    "Product tagging refresh triggered successfully!"
                );

                setMappingRefreshStatus(MappingSyncStatus.Syncing);

                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }

                pollStatus();
                intervalRef.current = setInterval(pollStatus, 5000);
            } else {
                showToast(
                    "danger",
                    "Failed to trigger mapping refresh. Please try again after some time."
                );
            }
        } catch (error) {
            console.error("Error triggering mapping refresh:", error);
            showToast(
                "danger",
                "An error occurred while triggering mapping refresh."
            );
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        // Immediate poll
        pollStatus();

        intervalRef.current = setInterval(pollStatus, 5000);

        return () => {
            isMountedRef.current = false;
            intervalRef.current && clearInterval(intervalRef.current);
        };
    }, [pollStatus]);

    return (
        <div
            className={`d-inline-flex align-items-center justify-content-between gap-3 bg-transparent border-0 p-0 ${className}`}
            style={style}
        >
            {mappingRefreshStatus !== null && (
                <div className="d-flex align-items-center gap-2">
                    <span
                        aria-hidden
                        className={`rounded-circle ${currentStatus === MappingSyncStatus.Syncing
                                ? "blink-animation"
                                : ""
                            }`}
                        style={{
                            width: 10,
                            height: 10,
                            backgroundColor: color,
                            display: "inline-block",
                            boxShadow: `0 0 0 3px ${color}1a`,
                        }}
                    />
                    <div className="d-flex align-items-center flex-wrap gap-1">
                        <span className="text-body-secondary fw-500">
                            Last Sync:
                        </span>
                        <span className="fw-semibold" style={{ color }}>
                            {label}
                        </span>
                        {currentStatus !== MappingSyncStatus.Syncing && (
                            <span className="text-body-secondary">
                                |{" "}
                                {tooltipContent ? (
                                    <Tooltip
                                        title={tooltipContent}
                                        placement="bottom"
                                    >
                                        <span style={{ cursor: "pointer" }}>
                                            {formattedEndTime}
                                        </span>
                                    </Tooltip>
                                ) : (
                                    formattedEndTime
                                )}
                            </span>
                        )}
                    </div>
                </div>
            )}
            <Button
                type="primary"
                size="middle"
                icon={<ReloadOutlined />}
                loading={isRefreshing}
                disabled={currentStatus === MappingSyncStatus.Syncing}
                onClick={handleRefreshClick}
                className="d-inline-flex align-items-center fw-600 px-3"
                style={{
                    height: 32,
                }}
            >
                {buttonText}
            </Button>
        </div>
    );
};

export default MappingRefreshWidget;
